import { Plus } from 'lucide-react'
import type { Overlay } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { Button } from '../../common/Button'
import { makeId } from '../../../lib/id'
import { OVERLAY_KIND_ICON, OVERLAY_KIND_LABEL, overlaySummary } from './overlayMeta'

/**
 * task spec §3-1: 未選択時の「要素」一覧。この Composition の Overlay を種類別に
 * 列挙（Segment は列挙しない — Segment はタイムラインで扱う）。旧 TabTitle /
 * TabLabels / TabEndCard の「一覧・追加」機能はここへ統合した（task spec §3-7）。
 * タイトル/終了カードは Composition につき 1 個の運用（旧 Tab* も find() 単数
 * 前提だった）なので、既に存在する間は追加ボタンを隠す。ラベル/テキストは複数可。
 */
export function ElementsList() {
  const composition = useEditorStore((s) => s.composition)
  const addOverlay = useEditorStore((s) => s.addOverlay)
  const select = useEditorStore((s) => s.select)

  if (!composition) return null
  const overlays = composition.tracks.overlays
  const hasTitle = overlays.some((o) => o.kind === 'title')
  const hasEnd = overlays.some((o) => o.kind === 'end')

  function addAndSelect(o: Overlay) {
    addOverlay(o)
    select({ kind: 'overlay', id: o.id })
  }

  return (
    <div className="p-3 border-b border-ink-border space-y-2 shrink-0" data-testid="elements-list">
      <div className="text-xs font-semibold text-text-muted">要素</div>

      {overlays.length === 0 ? (
        <p className="text-[11px] text-text-faint">まだ要素がありません。下のボタンから追加してください。</p>
      ) : (
        <div className="space-y-1 max-h-[220px] overflow-y-auto">
          {overlays.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => select({ kind: 'overlay', id: o.id })}
              data-testid="element-row"
              data-overlay-kind={o.kind}
              className="w-full flex items-center gap-2 rounded border border-ink-border bg-ink-surface2/50 px-2 py-1.5 text-left hover:border-white/20"
            >
              <span className="text-text-faint shrink-0">{OVERLAY_KIND_ICON[o.kind]}</span>
              <span className="flex-1 min-w-0 text-[11px] truncate">
                <span className="text-text-faint">{OVERLAY_KIND_LABEL[o.kind]}</span> ・ {overlaySummary(o)}
              </span>
              {o.kind !== 'logo' && (
                <span className="text-[10px] text-text-faint tabular-nums shrink-0">{o.startSec.toFixed(1)}s</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5">
        {!hasTitle && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus size={12} />}
            data-testid="element-add-title"
            onClick={() =>
              addAndSelect({
                kind: 'title',
                id: makeId('ov'),
                startSec: 0,
                durationSec: 2,
                text: composition.meta.title,
                vertical: true,
                venue: composition.meta.venue,
                position: 'center',
              })
            }
          >
            タイトル追加
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus size={12} />}
          data-testid="element-add-label"
          onClick={() =>
            addAndSelect({
              kind: 'label',
              id: makeId('ov'),
              startSec: 0,
              durationSec: 1.5,
              ja: '新しいラベル',
              style: 'bracket',
              position: 'left-upper',
            })
          }
        >
          ラベル追加
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus size={12} />}
          data-testid="element-add-free"
          onClick={() =>
            addAndSelect({
              kind: 'free',
              id: makeId('ov'),
              startSec: 0,
              durationSec: 2,
              text: '新しいテキスト',
              font: 'gothic',
              sizePx: 56,
              color: '#FFFFFF',
              strokeColor: '#000000',
              x: 0.5,
              y: 0.5,
              vertical: false,
            })
          }
        >
          テキスト追加
        </Button>
        {!hasEnd && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus size={12} />}
            data-testid="element-add-end"
            onClick={() =>
              addAndSelect({
                kind: 'end',
                id: makeId('ov'),
                startSec: 0,
                durationSec: 2,
                lines: [composition.meta.dateText ?? '会期未設定'],
                vertical: true,
                venue: composition.meta.venue,
              })
            }
          >
            終了カード追加
          </Button>
        )}
      </div>
    </div>
  )
}
